import { c as _c } from "react/compiler-runtime";
import { ReactNode, useEffect, useRef, useState } from "react";
import { XIcon } from "lucide-react";
import clsx from "classnames";
import { useTranslations } from "modules/i18n/hooks/useTranslations";
import { useAnonymousNewslettersContext, useSubscribedNewslettersContext } from "modules/newsletters/contexts";
interface StickySubscriptionBarProps {
  children: ReactNode;
}

/**
 * Sticky container that manages sticky behavior, close logic, and count badge.
 * Shows a count badge when not subscribed and a close button when subscribed.
 */
export const StickySubscriptionBar = t0 => {
  "use memo";

  const $ = _c(21);
  const {
    children
  } = t0;
  const [isClosed, setIsClosed] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const sentinelRef = useRef(null);
  const {
    ids: newsletterListIds
  } = useSubscribedNewslettersContext();
  const {
    isSubscribed
  } = useAnonymousNewslettersContext();
  const t = useTranslations("newsletters");
  const selectedCount = newsletterListIds.size;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const sentinel = sentinelRef.current;
      if (!sentinel) {
        return;
      }
      const observer = new IntersectionObserver(t3 => {
        const [entry] = t3;
        setIsStuck(entry.intersectionRatio < 1);
      }, {
        threshold: [1]
      });
      observer.observe(sentinel);
      return () => observer.disconnect();
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div ref={sentinelRef} className="pointer-events-none -mt-px h-px" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = !isClosed;
  const t5 = isStuck && !isClosed;
  let t6;
  if ($[3] !== t4 || $[4] !== t5) {
    t6 = clsx("z-10 mr-[calc(-50vw+50%)] ml-[calc(-50vw+50%)] w-screen bg-white py-4", {
      "sticky top-0": t4,
      "border-b border-gray-200 shadow-lg": t5
    });
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== isClosed || $[7] !== isStuck || $[8] !== isSubscribed) {
    t7 = isSubscribed && !isClosed && isStuck && <div className="absolute top-4 right-4"><button onClick={() => setIsClosed(true)} className="flex size-10 items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100"><XIcon className="size-5" /></button></div>;
    $[6] = isClosed;
    $[7] = isStuck;
    $[8] = isSubscribed;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== children || $[11] !== t7) {
    t8 = <div className="flex flex-col items-center justify-center px-4 sm:px-6">{children}{t7}</div>;
    $[10] = children;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== isSubscribed || $[14] !== selectedCount || $[15] !== t) {
    t9 = !isSubscribed && <div className="absolute bottom-0 left-1/2 -translate-x-1/2"><div className="inline-flex translate-y-1/2 rounded-full bg-indigo-500 px-3 py-1 text-center text-sm whitespace-nowrap text-white" aria-live="polite">{selectedCount === 0 ? t("v2.sticky_bar.select_at_least_one") : t(`v2.sticky_bar.selected_count.${selectedCount === 1 ? "one" : "other"}`, {
          count: selectedCount
        })}</div></div>;
    $[13] = isSubscribed;
    $[14] = selectedCount;
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t8 || $[19] !== t9) {
    t10 = <>{t3}<div data-testid="sticky-subscription-bar" className={t6}>{t8}{t9}</div></>;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
};